import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Toaster, toast } from 'sonner';
import { Euro, Users, TrendingUp, Building2, CheckCircle2, ArrowRight, Calculator, Clock, Award } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPLOYERS_IMAGE = "https://images.pexels.com/photos/7433843/pexels-photo-7433843.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const benefits = [
  {
    icon: Euro,
    title: "Exonération de charges",
    description: "Cotisations sociales exonérées jusqu'à 50% du SMIC. Réduction FILLON au-delà",
    highlight: "Charges réduites"
  },
  {
    icon: TrendingUp,
    title: "Aide à l'embauche 2026",
    description: "Aide État : 4 500€ (PME < 250 sal.) ou 1 500€ (grandes entreprises) pour un Bac+2. Décret n°2026-168",
    highlight: "Jusqu'à 4 500€"
  },
  {
    icon: Clock,
    title: "Apprenti quasi plein temps",
    description: "Présent 80% du temps - le rythme le plus productif",
    highlight: "80%"
  },
  {
    icon: Users,
    title: "Profil formé sur mesure",
    description: "Programme calibré : vente B2B, négociation, CRM, closing",
    highlight: "Sur mesure"
  },
  {
    icon: Calculator,
    title: "Taxe d'apprentissage",
    description: "Déductible et fléchable vers l'Institut BIA",
    highlight: "Déductible"
  },
  {
    icon: Award,
    title: "Vivier de talents",
    description: "60% des apprentis recrutés en CDI à l'issue de la formation",
    highlight: "60% CDI"
  }
];

const costSimulation = {
  salary: "9 300 € - 22 400 €",
  charges: "Réduites",
  aid: "- 4 500 €",
  netCost: "Variable selon âge",
  productiveValue: "18 000 € - 25 000 €",
  classicEmployee: "35 000 € - 45 000 €"
};

export const Employers = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    sector: '',
    company_size: '',
    message: '',
    apprentice_count: 1
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contacts/employer`, {
        ...formData,
        apprentice_count: parseInt(formData.apprentice_count)
      });
      toast.success('Demande envoyée avec succès !', {
        description: 'Notre équipe partenariats vous contactera sous 48h.'
      });
      setFormData({
        company_name: '',
        contact_name: '',
        email: '',
        phone: '',
        sector: '',
        company_size: '',
        message: '',
        apprentice_count: 1
      });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi', {
        description: 'Veuillez réessayer plus tard.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="employers-page">
      <Toaster position="top-right" richColors />
      
      {/* Hero Section */}
      <section className="bg-[#0B1F3A] py-24" data-testid="employers-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
                Espace Employeurs
              </span>
              <h1 className="font-serif-heading text-4xl sm:text-5xl font-semibold text-white mb-6">
                Recrutez votre futur commercial
              </h1>
              <div className="w-16 h-0.5 bg-[#C8A75A] mb-6"></div>
              <p className="text-[#F5F5F5]/90 text-lg mb-8 leading-relaxed">
                Un apprenti formé sur le terrain, opérationnel en 2 mois, 
                avec une aide de l'État et des charges réduites (contrat d'apprentissage).
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="bg-white/10 px-4 py-2 text-white text-sm">
                  <span className="text-[#C8A75A] font-semibold">Charges</span> réduites
                </div>
                <div className="bg-white/10 px-4 py-2 text-white text-sm">
                  <span className="text-[#C8A75A] font-semibold">4 500€</span> d'aide (PME)
                </div>
                <div className="bg-white/10 px-4 py-2 text-white text-sm">
                  <span className="text-[#C8A75A] font-semibold">80%</span> présence
                </div>
              </div>

              <a href="#partenariat">
                <Button className="bg-[#C8A75A] text-[#0B1F3A] hover:bg-[#DDBF78] rounded-none px-8 py-6" data-testid="employers-hero-cta">
                  Devenir partenaire
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </a>
            </div>
            
            <div className="relative">
              <img 
                src={EMPLOYERS_IMAGE}
                alt="Partenariat entreprise"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white" data-testid="employer-benefits">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Vos Avantages
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-[#0B1F3A]">
              Pourquoi recruter via l'Institut BIA ?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="border border-[#E6E6E6] p-8 hover:border-[#C8A75A] transition-colors hover-lift"
                data-testid={`employer-benefit-${index}`}
              >
                <benefit.icon className="text-[#C8A75A] mb-4" size={32} strokeWidth={1.5} />
                <div className="bg-[#0B1F3A] text-[#C8A75A] text-sm font-semibold px-3 py-1 inline-block mb-4">
                  {benefit.highlight}
                </div>
                <h3 className="font-serif-heading text-xl font-semibold text-[#0B1F3A] mb-2">
                  {benefit.title}
                </h3>
                <p className="text-[#4A4A4A] text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Simulation */}
      <section className="py-24 bg-[#F5F5F5]" data-testid="cost-simulation">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
                Simulation
              </span>
              <h2 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-[#0B1F3A] mb-6">
                Coût réel d'un apprenti
              </h2>
              <div className="w-16 h-0.5 bg-[#C8A75A] mb-6"></div>
              <p className="text-[#4A4A4A] mb-8 leading-relaxed">
                Grâce aux aides de l'État et à l'exonération des charges patronales, 
                le coût net d'un apprenti est inférieur à 5000€ par an. 
                Soit une économie de 23 000€ par rapport à un recrutement classique.
              </p>

              <div className="bg-[#0B1F3A] p-6 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <Calculator className="text-[#C8A75A]" size={24} />
                  <span className="font-semibold">Économie vs recrutement classique</span>
                </div>
                <div className="font-serif-heading text-4xl text-[#C8A75A]">
                  23 000 €/an
                </div>
              </div>
            </div>

            {/* Cost Table */}
            <div className="bg-white p-8 border border-[#E6E6E6]">
              <h3 className="font-serif-heading text-xl font-semibold text-[#0B1F3A] mb-6">
                Simulation annuelle (apprenti 18-29 ans)
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-[#E6E6E6]">
                  <span className="text-[#4A4A4A]">Salaire brut annuel</span>
                  <span className="font-semibold text-[#0B1F3A]">{costSimulation.salary}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-[#E6E6E6]">
                  <span className="text-[#4A4A4A]">Charges patronales</span>
                  <span className="font-semibold text-[#C8A75A]">{costSimulation.charges}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-[#E6E6E6]">
                  <span className="text-[#4A4A4A]">Aide à l'embauche (ASP)</span>
                  <span className="font-semibold text-green-600">{costSimulation.aid}</span>
                </div>
                <div className="flex justify-between items-center py-4 bg-[#0B1F3A] px-4 -mx-4">
                  <span className="text-white font-semibold">Coût net annuel</span>
                  <span className="font-serif-heading text-2xl text-[#C8A75A]">{costSimulation.netCost}</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-[#E6E6E6]">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-[#4A4A4A]">Valeur productive (80% présence)</span>
                  <span className="text-sm font-semibold text-[#0B1F3A]">{costSimulation.productiveValue}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#4A4A4A]">Salarié équivalent (avec charges)</span>
                  <span className="text-sm font-semibold text-[#0B1F3A]">{costSimulation.classicEmployee}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Process */}
      <section className="py-24 bg-white" data-testid="partnership-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Processus
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-[#0B1F3A]">
              Devenir partenaire en 4 étapes
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Contact Institut BIA", desc: "Décrivez votre besoin et votre secteur" },
              { step: "02", title: "Présélection candidats", desc: "Profils pré-qualifiés sous 15 jours" },
              { step: "03", title: "Entretien & Signature", desc: "Dépôt OPCO en 48h" },
              { step: "04", title: "Démarrage J+0", desc: "Apprenti opérationnel dès la 1ère semaine" }
            ].map((item, index) => (
              <div key={index} className="text-center" data-testid={`partnership-step-${index}`}>
                <div className="w-16 h-16 bg-[#0B1F3A] flex items-center justify-center mx-auto mb-4">
                  <span className="text-[#C8A75A] font-serif-heading text-2xl font-semibold">
                    {item.step}
                  </span>
                </div>
                <h3 className="font-semibold text-[#0B1F3A] mb-2">{item.title}</h3>
                <p className="text-[#4A4A4A] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Sectors */}
      <section className="py-24 bg-[#0B1F3A]" data-testid="target-sectors">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Secteurs
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-white">
              Nos secteurs partenaires
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: "Industrie & BTP", icon: Building2 },
              { title: "Tech & Services", icon: TrendingUp },
              { title: "Distribution & Négoce", icon: Users }
            ].map((sector, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur border border-white/10 p-8 text-center hover:border-[#C8A75A] transition-colors"
                data-testid={`sector-${index}`}
              >
                <sector.icon className="mx-auto text-[#C8A75A] mb-4" size={40} strokeWidth={1.5} />
                <h3 className="text-white font-semibold text-xl">{sector.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="partenariat" className="py-24 bg-white scroll-mt-20" data-testid="employer-contact-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Partenariat
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-[#0B1F3A] mb-4">
              Contactez notre équipe
            </h2>
            <p className="text-[#4A4A4A]">
              Décrivez votre besoin et nous vous proposerons des profils pré-qualifiés sous 15 jours.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-[#F5F5F5] p-8 md:p-12 border border-[#E6E6E6]" data-testid="employer-contact-form">
            {/* Company Info */}
            <div className="mb-8">
              <h3 className="text-[#C8A75A] uppercase tracking-[0.2em] text-xs font-semibold mb-6">
                Informations entreprise
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="company_name" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                    Nom de l'entreprise *
                  </Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]"
                    data-testid="input-company-name"
                  />
                </div>
                <div>
                  <Label htmlFor="sector" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                    Secteur d'activité *
                  </Label>
                  <Select onValueChange={(value) => handleSelectChange('sector', value)} value={formData.sector}>
                    <SelectTrigger className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]" data-testid="select-sector">
                      <SelectValue placeholder="Sélectionnez un secteur" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="industrie-btp">Industrie & BTP</SelectItem>
                      <SelectItem value="tech-services">Tech & Services</SelectItem>
                      <SelectItem value="distribution-negoce">Distribution & Négoce</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="company_size" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                    Taille de l'entreprise *
                  </Label>
                  <Select onValueChange={(value) => handleSelectChange('company_size', value)} value={formData.company_size}>
                    <SelectTrigger className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]" data-testid="select-company-size">
                      <SelectValue placeholder="Sélectionnez une taille" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-10">1-10 salariés</SelectItem>
                      <SelectItem value="11-50">11-50 salariés</SelectItem>
                      <SelectItem value="51-200">51-200 salariés</SelectItem>
                      <SelectItem value="200+">Plus de 200 salariés</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="apprentice_count" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                    Nombre d'apprentis souhaités *
                  </Label>
                  <Input
                    id="apprentice_count"
                    name="apprentice_count"
                    type="number"
                    min="1"
                    value={formData.apprentice_count}
                    onChange={handleChange}
                    required
                    className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]"
                    data-testid="input-apprentice-count"
                  />
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="mb-8">
              <h3 className="text-[#C8A75A] uppercase tracking-[0.2em] text-xs font-semibold mb-6">
                Contact
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="contact_name" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                    Nom du contact *
                  </Label>
                  <Input
                    id="contact_name"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleChange}
                    required
                    className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]"
                    data-testid="input-contact-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]"
                    data-testid="input-employer-email"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="phone" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                    Téléphone *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]"
                    data-testid="input-employer-phone"
                  />
                </div>
              </div>
            </div>

            {/* Message */}
            <div className="mb-8">
              <Label htmlFor="message" className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">
                Décrivez votre besoin *
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                placeholder="Décrivez le poste, les missions, vos attentes..."
                className="mt-2 rounded-none border-[#E6E6E6] focus:border-[#0B1F3A]"
                data-testid="textarea-employer-message"
              />
            </div>

            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full bg-[#C8A75A] text-[#0B1F3A] hover:bg-[#DDBF78] rounded-none py-6 text-base font-semibold"
              data-testid="submit-employer-contact"
            >
              {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Employers;
